import React from 'react';
import PropTypes from 'prop-types';

import BigNumber from 'bignumber.js';

import { ThemeProvider } from 'react-jss';

import { debounce } from 'debounce';

import { CONTACT_FIELDS_MAP } from './config';
import defaultPalette from './palette';
import * as storage from './common/utils/storage';
import * as math from './common/utils/math';
import * as convert from './common/utils/convert';
import decorate from './common/utils/decorate';
import AddressInput from './common/components/AddressInput';
import DeliveryWidgetContext from './contexts/DeliveryWidgetContext';
import Widget from './pages/Widget';

const DELIVERY_WIDGET_BIG_WIDTH = 550;

const DEFAULT_PRICE_ROUND = (value, currency, type = 'round') =>
  convert.toBigNumber(value).decimalPlaces(2);

const DEFAULT_PRICE_FORMAT = (value, currency, type = 'jsx') =>
  convert.toBigNumber(value).toFixed(2).replace(/\.0+$/, '');

const DEFAULT_API_REQUEST = (method, param) =>
  Promise.reject(new Error('apiRequest required'));

const DEFAULT_API_GET_CLIENT_TOKEN_GENERATE = (method, param) =>
  Promise.reject(new Error('apiGetClientTokenGenerate required'));

const DEFAULT_API_GET_CLIENT_VISITOR = (method, param) =>
  Promise.reject(new Error('apiGetClientVisitor required'));

const DEFAULT_API_GET_PORTAL_ARTICLE = (method, param) =>
  Promise.reject(new Error('apiGetPortalArticle required'));

const DEFAULT_SCROLL_TOP = () => {
  if (typeof window === 'undefined') return;

  let count = 10;
  let lastScrollY = window.scrollY;

  if (!lastScrollY) return;

  const onScroll = (event) => {
    if (window.scrollY > lastScrollY) count = 0;
  };

  window.addEventListener('scroll', onScroll);

  const scrollTop = () => {
    const scrollY = window.scrollY;

    if (count > 0 && lastScrollY !== scrollY) {
      lastScrollY = scrollY;

      setTimeout(scrollTop, 100);

      return;
    }

    count--;

    if (count > 0 && scrollY > 0) {
      window.scrollTo(0, 0);

      setTimeout(scrollTop, 100);
    } else {
      window.removeEventListener('scroll', onScroll);
    }
  };

  scrollTop();
};

const CACHE_DATA = {};

export default decorate(
  class extends React.PureComponent {
    static displayName = 'DeliveryWidget';

    static decorateWith = {};

    static propTypes = {
      type: PropTypes.string.isRequired,
      lang: PropTypes.string.isRequired,
      shop: PropTypes.string.isRequired,
      site: PropTypes.number,
      cart: PropTypes.object,
      group_order: PropTypes.object,
      documents: PropTypes.arrayOf(PropTypes.number),
      request: PropTypes.number,
      warehouse: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
      visitor: PropTypes.object,
      authorizeType: PropTypes.string.isRequired,
      referralParent: PropTypes.string,
      editor: PropTypes.object,
      palette: PropTypes.object,
      theme: PropTypes.string.isRequired,
      requestSuccessUrl: PropTypes.string,
      requestErrorUrl: PropTypes.string,
      requestIsError: PropTypes.bool.isRequired,

      localityData: PropTypes.object,
      onLocalityData: PropTypes.func,

      currencyMap: PropTypes.object,
      priceRound: PropTypes.func,
      priceFormat: PropTypes.func,
      apiRequest: PropTypes.func,
      apiGetClientTokenGenerate: PropTypes.func,
      apiGetClientVisitor: PropTypes.func,
      apiGetPortalArticle: PropTypes.func,
      scrollTop: PropTypes.func,

      onPrevPage: PropTypes.func
    };

    static defaultProps = {
      type: 'delivery',
      lang: 'en',
      shop: 'portal',
      authorizeType: 'email-sms',
      theme: 'light',
      requestIsError: false
    };

    constructor(props) {
      super(props);

      this.update = 0;

      this.state = {
        isSSR: true,
        isSmall: false,
        deliveryInfo: undefined,
        langStrings: undefined,
        methodQuery: undefined,
        tariffList: {}
      };
    }

    deliveryWidgetRef = (ref) => {
      this.deliveryWidgetRefValue = ref;

      this.onDeliveryWidgetWidth();
    };

    onDeliveryWidgetWidth = () => {
      const container = this.deliveryWidgetRefValue;

      if (!container) return;

      const width = container.clientWidth;

      this.setState((state) => {
        const isSmall = width < DELIVERY_WIDGET_BIG_WIDTH;

        return state.isSmall !== isSmall ? { ...state, isSmall } : state;
      });
    };

    loadDataCache = (contextData, name, method, param) => {
      const key = contextData.lang + '-' + name;

      if (!(key in CACHE_DATA)) {
        CACHE_DATA[key] = null;

        contextData
          .apiRequest(method, param || {})
          .then((data) => {
            CACHE_DATA[key] = data.list || [];
            setTimeout(
              () => this.setState((state) => ({ ...state, [name]: data })),
              1
            );
          })
          .catch((error) => {
            console.error(error);
            CACHE_DATA[key] = [];
            setTimeout(
              () => this.setState((state) => ({ ...state, [name]: [] })),
              1
            );
          });
      }

      contextData[name] = CACHE_DATA[key];
    };

    deliveryInfo = (contextData) => {
      const { onNextPage } = this.props;

      const {
        shop,
        site,
        currency,
        cart,
        editor,

        apiRequest
      } = contextData;

      if (editor) return true;

      if (this.state.deliveryInfo === undefined) {
        this.state.deliveryInfo = null;

        const orderCreate = (deliveryInfo) =>
          apiRequest('delivery/order/create', {
            shop,
            site,
            currency,
            cart: cart.list
          })
            .then(onNextPage)
            .catch((error) =>
              this.setState((state) => ({ ...state, deliveryInfo }))
            );

        apiRequest('delivery/method/info', {
          shop,
          site,
          currency,
          cart: cart.list
        })
          .then((deliveryInfo) => {
            if (deliveryInfo.delivery) {
              this.setState((state) => ({ ...state, deliveryInfo }));
            } else {
              orderCreate(deliveryInfo);
            }
          })
          .catch((error) => orderCreate({}));
      }

      contextData.deliveryInfo = this.state.deliveryInfo;

      return this.state.deliveryInfo;
    };

    onLoadTariffList = debounce((contextData) => {
      this.state.tariffList[contextData.lang] = null;

      const promises = contextData.serviceList.map((service) => {
        return contextData.apiRequest('delivery/tariff/list', {
          service: service.code
        });
      });

      Promise.allSettled(promises).then((data) => {
        const tariffList = [];

        data.forEach((item, serviceId) => {
          if (item.status !== 'fulfilled' || !item.value?.list) return;

          const service = contextData.serviceList[serviceId];

          item.value.list.forEach((item) => {
            tariffList.push({
              ...item,

              service
            });
          });
        });

        this.setState((state) => ({
          ...state,
          tariffList: { ...state.tariffList, [contextData.lang]: tariffList }
        }));
      });
    }, 100);

    onLoadMethodQuery = debounce((contextData) => {
      this.state.methodQuery = null;

      const update = ++this.update;

      const serviceMap = {};

      const promises = contextData.serviceList
        .filter((service) => {
          if (serviceMap[service.code]) return false;
          return (serviceMap[service.code] = true);
        })
        .map((service) =>
          contextData.apiRequest('delivery/method/query', {
            shop: contextData.shop,
            site: contextData.site,
            currency: contextData.currency,
            service: service.code,
            locality: AddressInput.buildValue(
              'delivery',
              'locality',
              contextData.localityData
            ),
            cart: contextData.cart.list,
            warehouse: contextData.warehouse.id
          })
        );

      Promise.allSettled(promises).then((data) => {
        if (update !== this.update) return;

        const methodQuery = {};

        data.forEach((item, serviceId) => {
          if (item.status !== 'fulfilled' || !item.value) return;

          const service = contextData.serviceList[serviceId];

          Object.entries(item.value).forEach(([id, tariffs]) => {
            if (!tariffs?.length) return;

            const last = methodQuery[id];

            const current = (methodQuery[id] = {
              price: [Infinity, 0.0],
              days: [Infinity, 0],
              tariffs: tariffs.map((item) => ({ ...item, service }))
            });

            tariffs.forEach((tariff) => {
              current.price[0] = Math.min(current.price[0], tariff.price);
              current.price[1] = Math.max(current.price[1], tariff.price);

              current.days[0] = Math.min(current.days[0], tariff.days[0]);
              current.days[1] = Math.max(current.days[1], tariff.days[1]);
            });

            if (last) {
              current.price[0] = Math.min(last.price[0], current.price[0]);
              current.price[1] = Math.max(last.price[1], current.price[1]);

              current.days[0] = Math.min(last.days[0], current.days[0]);
              current.days[1] = Math.max(last.days[1], current.days[1]);

              current.tariffs.push(...last.tariffs);
            }
          });
        });

        Object.values(methodQuery).forEach((query) => {
          query.companies = [];

          query.tariffs.forEach((tariff) => {
            let company = query.companies.find(
              (item) => item.id === tariff.company
            );

            if (!company)
              query.companies.push(
                (company = {
                  id: tariff.company,
                  price: [Infinity, 0.0],
                  days: [Infinity, 0]
                })
              );

            company.price[0] = Math.min(company.price[0], tariff.price);
            company.price[1] = Math.max(company.price[1], tariff.price);

            company.days[0] = Math.min(company.days[0], tariff.days[0]);
            company.days[1] = Math.max(company.days[1], tariff.days[1]);
          });

          query.tariffs.sort((item1, item2) => {
            if (item1.price < item2.price) return -1;

            if (item1.price > item2.price) return 1;

            if (item1.days[0] < item2.days[0]) return -1;

            if (item1.days[0] > item2.days[0]) return 1;

            return item1.id - item2.id;
          });
        });

        //console.log('methodQuery:', methodQuery);

        this.setState((state) =>
          update === this.update ? { ...state, methodQuery } : state
        );
      });
    }, 100);

    onClearQuery = debounce((callback) => {
      if (this.state.methodQuery === undefined) return;

      this.setState((state) => {
        if (state.methodQuery === undefined) return state;

        state = {
          ...state,

          deliveryInfo: undefined,
          methodQuery: undefined
        };

        setTimeout(callback, 1);

        return state;
      });
    }, 10);

    componentDidMount() {
      window.addEventListener('resize', this.onDeliveryWidgetWidth);

      setTimeout(
        () => this.setState((state) => ({ ...state, isSSR: false })),
        1
      );
    }

    componentWillUnmount() {
      window.removeEventListener('resize', this.onDeliveryWidgetWidth);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {}

    render() {
      if (this.state.isSSR) return null;

      const {
        type,
        lang,
        shop,
        site,
        currency,
        warehouse,
        visitor,
        cart,
        group_order,
        documents,
        request,
        authorizeType,
        referralParent,
        editor,
        palette,
        theme,
        requestSuccessUrl,
        requestErrorUrl,
        requestIsError,

        localityData,
        onLocalityData,

        currencyMap,
        priceRound,
        priceFormat,
        apiRequest,
        apiGetClientTokenGenerate,
        apiGetClientVisitor,
        apiGetPortalArticle,
        scrollTop,

        onNextPage,
        onPrevPage
      } = this.props;

      const langStrings = this.state.langStrings;

      if (
        langStrings === undefined ||
        (langStrings && langStrings.lang !== lang)
      ) {
        this.state.langStrings = null;

        const cacheKey = 'langStrings-' + lang;

        (
          CACHE_DATA[cacheKey] ||
          (CACHE_DATA[cacheKey] = apiRequest('client/lang/strings', {
            list: ['delivery.widget.*']
          }))
        )
          .then((data) =>
            this.setState((state) => ({
              ...state,
              langStrings: { ...data, lang }
            }))
          )
          .catch((error) =>
            this.setState((state) => ({ ...state, langStrings: { lang } }))
          );
      }

      if (!this.state.address) {
        if (editor)
          this.state.address = {
            address: { ...editor.address },
            recipient: { ...editor.recipient },
            note: editor.note
          };
      }

      const contextData = {
        type,
        lang,
        shop,
        site,
        currency,
        cart,
        group_order,
        warehouse: warehouse?.id ? warehouse : { id: warehouse || null },
        visitor,
        authorizeType,
        referralParent,
        editor,

        langString: (code) =>
          !code
            ? null
            : math.isObject(code)
            ? code[lang]
            : langStrings?.['delivery.widget.' + code] || code,

        apiRequest: apiRequest || DEFAULT_API_REQUEST,
        apiGetClientTokenGenerate:
          apiGetClientTokenGenerate || DEFAULT_API_GET_CLIENT_TOKEN_GENERATE,
        apiGetClientVisitor:
          apiGetClientVisitor || DEFAULT_API_GET_CLIENT_VISITOR,
        apiGetPortalArticle:
          apiGetPortalArticle || DEFAULT_API_GET_PORTAL_ARTICLE,
        daysFormat: (days) =>
          days && days[1]
            ? contextData
                .langString('days')
                .replace(
                  '%',
                  days[0] === days[1] ? days[0] : days[0] + '-' + days[1]
                )
            : contextData.langString('today'),
        scrollTop: scrollTop || DEFAULT_SCROLL_TOP,

        isSmall: this.state.isSmall,
        isLangsLoading: !langStrings
      };

      if (priceRound) {
        contextData.priceRound = priceRound;
      } else if (currencyMap) {
        contextData.priceRound = (value, curr, type = 'round') =>
          roundPrice(currencyMap, curr || currency, value, false, type);
      } else {
        contextData.priceRound = DEFAULT_PRICE_ROUND;
      }

      if (priceFormat) {
        contextData.priceFormat = priceFormat;
      } else if (currencyMap) {
        contextData.priceFormat = (value, curr, type = 'jsx') =>
          formatPrice(currencyMap, curr || currency, value, type);
      } else {
        contextData.priceFormat = DEFAULT_PRICE_FORMAT;
      }

      const widgetProps = {
        onClearQuery: this.onClearQuery,

        currency: currency,
        warehouse: contextData.warehouse,

        onNextPage: onNextPage,
        onPrevPage: onPrevPage
      };

      if (type === 'delivery') {
        // contextData

        contextData.localityData = onLocalityData
          ? localityData
          : this.state.localityData;
        contextData.address = this.state.address;

        contextData.service = this.state.service;
        contextData.method = this.state.method;
        contextData.methodQuery = this.state.methodQuery;
        contextData.company = this.state.company;
        contextData.tariff = this.state.tariff;
        contextData.tariffList = this.state.tariffList[lang];
        contextData.pickupPoint = this.state.pickupPoint;

        if (this.deliveryInfo(contextData)) {
          this.loadDataCache(
            contextData,
            'serviceList',
            'delivery/service/list'
          );
          this.loadDataCache(contextData, 'methodList', 'delivery/method/list');
          this.loadDataCache(
            contextData,
            'companyList',
            'delivery/company/list'
          );

          if (
            this.state.tariffList[lang] === undefined &&
            contextData.serviceList
          )
            this.onLoadTariffList(contextData);

          if (
            this.state.methodQuery === undefined &&
            contextData.serviceList &&
            contextData.localityData
          )
            this.onLoadMethodQuery(contextData);
        }

        // widgetProps

        widgetProps.localityData = onLocalityData
          ? localityData
          : this.state.localityData;
        widgetProps.onLocalityData = onLocalityData
          ? onLocalityData
          : (localityData) =>
              this.setState((state) => ({ ...state, localityData }));

        widgetProps.service = this.state.service;
        widgetProps.method = this.state.method;
        widgetProps.onMethod = (service, method, company, tariff) => {
          this.setState((state) => ({
            ...state,

            service,
            method,
            company,
            tariff,

            address: {
              ...state.address,

              address: {
                ...state.address?.address,

                locality: AddressInput.buildValue(
                  'delivery',
                  'locality',
                  contextData.localityData
                )
              },

              warehouse: contextData.warehouse.id,
              service: service.code,
              method: method.id,
              company: company.id,
              tariff: tariff.id,
              point: null
            }
          }));
        };
        widgetProps.company = this.state.company;
        widgetProps.tariff = this.state.tariff;

        widgetProps.pickupPoint = this.state.pickupPoint;
        widgetProps.onPickupPoint = (pickupPoint) => {
          this.setState((state) => ({
            ...state,

            pickupPoint,

            address: {
              ...state.address,

              warehouse: contextData.warehouse.id,
              point:
                pickupPoint && state.method.code === 'pickup'
                  ? {
                      id: pickupPoint.id,
                      title: pickupPoint.title,
                      address: pickupPoint.address
                    }
                  : undefined
            }
          }));
        };

        widgetProps.address = this.state.address;
        widgetProps.onAddress = (address) => {
          this.setState((state) => {
            if (!address) {
              const addressList = state.deliveryInfo?.address;

              if (addressList?.length) address = addressList[0];
            }

            state = { ...state, address };

            state.service =
              (address &&
                contextData.serviceList?.find(
                  (service) => service.code === address.service
                )) ||
              null;
            state.method =
              (address &&
                contextData.methodList?.find(
                  (method) => method.id === address.method
                )) ||
              null;
            state.company =
              (address &&
                contextData.companyList?.find(
                  (company) => company.id === address.company
                )) ||
              null;
            state.tariff =
              (state.service &&
                contextData.tariffList?.find(
                  (tariff) =>
                    tariff.service.id === state.service.id &&
                    tariff.id === address.tariff
                )) ||
              null;
            state.pickupPoint = address?.point || null;

            return state;
          });
        };
        widgetProps.getAddressField = (name) => {
          const field = CONTACT_FIELDS_MAP[name];

          if (!field) return undefined;

          let container = this.state.address;

          if (!container) return undefined;

          if (field.path) container = container[field.path];

          if (!container) return undefined;

          return container[name];
        };
        widgetProps.setAddressField = (name, value) => {
          const field = CONTACT_FIELDS_MAP[name];

          if (!field) return undefined;

          this.setState((state) => {
            state = { ...state };

            let container = state.address;

            if (!container)
              state.address = container = {
                warehouse: contextData.warehouse.id
              };

            let valueContainer = container;

            if (field.path) valueContainer = container[field.path];

            if (!valueContainer) container[field.path] = valueContainer = {};

            valueContainer[name] = value;

            return state;
          });
        };
      } else if (type === 'payment') {
        contextData.documents = documents;
        contextData.request = request;

        contextData.requestSuccessUrl = requestSuccessUrl;
        contextData.requestErrorUrl = requestErrorUrl;
        contextData.requestIsError = requestIsError;
      }

      const themeData = math.deepMerge(
        {},
        defaultPalette.light,
        defaultPalette[theme],
        palette?.light,
        palette?.[theme]
      );

      themeData.name = theme;

      themeData.standarts = (component, state, type) => {
        return {
          fontFamily:
            themeData[component]?.font?.family ?? themeData.font.family,
          fontSize: themeData[component]?.font?.size ?? themeData.font.size,
          color: themeData.getColor(component, 'basic', 'contrast')
        };
      };

      themeData.get = (component, path) =>
        math.getField(themeData[component], path) ??
        math.getField(themeData, path);

      themeData.getColor = (
        component,
        name,
        property,
        red,
        green,
        blue,
        alpha
      ) => {
        const value =
          themeData[component]?.colors?.[name]?.[property] ??
          themeData.colors[name]?.[property] ??
          themeData[component]?.colors?.basic[property] ??
          themeData.colors.basic[property] ??
          'inherit';

        if (
          math.isNone(red) &&
          math.isNone(green) &&
          math.isNone(blue) &&
          math.isNone(alpha)
        )
          return value;

        const color = math.parseColor(value);

        if (math.isNone(red)) red = color[0];

        if (math.isNone(green)) green = color[1];

        if (math.isNone(blue)) blue = color[2];

        if (math.isNone(alpha)) alpha = color[3];

        if (
          red == color[0] &&
          green == color[1] &&
          blue == color[2] &&
          alpha == color[3]
        )
          return value;

        return `rgba(${red * 255}, ${green * 255}, ${blue * 255}, ${alpha})`;
      };

      themeData.getFactor = (component, path, factor) => {
        const size = themeData.get(component, path);

        if (typeof size === 'number' || size instanceof Number) {
          return Number((size * factor).toFixed(2));
        } else {
          return String(size || '0').replace(/(\d+\.?\d*|\d*\.?\d+)/, (value) =>
            Number((Number(value) || 0) * factor).toFixed(2)
          );
        }
      };

      themeData.getBoxShadow = (component) => {
        const x = themeData.get(component, 'shadow.x');
        const y = themeData.get(component, 'shadow.y');
        const blur = themeData.get(component, 'shadow.blur');
        const spread = themeData.get(component, 'shadow.spread');
        const color = themeData.get(component, 'shadow.color');

        if (!x && !y && !blur && !spread) return 'none';

        return [x, y, blur, spread, color];
      };

      return (
        <DeliveryWidgetContext.Provider value={contextData}>
          <ThemeProvider theme={themeData}>
            <div ref={this.deliveryWidgetRef}>
              <Widget {...widgetProps} />
            </div>
          </ThemeProvider>
        </DeliveryWidgetContext.Provider>
      );
    }
  }
);

const formatPrice = (currencyMap, currency, value, type = 'jsx') => {
  currency = currencyMap[currency] || { code: '', format: '%', scale: 2 };
  value = roundPrice(currencyMap, currency, value, true);

  if (type === 'simple') {
    return value;
  } else if (type === 'string') {
    return currency.format.replace('%', value);
  } else if (type === 'jsx') {
    const currencyCode =
      currency.code === 'rur' ? 'RUB' : currency.code.toUpperCase();

    return currency.format.match(/(%|\s+|[^%\s]+)/g).map((item, index) =>
      /\s+/.test(item) ? (
        <React.Fragment key={index}>&nbsp;</React.Fragment>
      ) : item === '%' ? (
        <span key={index} itemProp="price" content={value}>
          {value}
        </span>
      ) : !/\s/.test(item) ? (
        <span key={index} itemProp="priceCurrency" content={currencyCode}>
          {item}
        </span>
      ) : (
        item
      )
    );
  } else {
    return value;
  }
};

const roundPrice = (
  currencyMap,
  currency,
  value,
  stringify = false,
  type = 'floor'
) => {
  currency = currencyMap[currency];
  value = convert.toBigNumber(value);

  let roundMode = BigNumber.ROUND_HALF_EVEN;

  if (type === 'ceil') {
    roundMode = BigNumber.ROUND_UP;
  } else if (type === 'floor') {
    roundMode = BigNumber.ROUND_DOWN;
  } else if (type === 'round') {
    roundMode = BigNumber.ROUND_HALF_EVEN;
  }

  if (stringify) {
    value = value.toFixed(currency, roundMode).replace(/\.0+$/, '');
  } else {
    value = value.decimalPlaces(currency, roundMode);
  }

  return value;
};
